/**
 * Общие части кабинета хозяйства и карточки контура (agroai-6al.37):
 * легенда-фильтр рядом с картой и параметр вкладки в адресе.
 */

import { useCallback, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { num } from '../../lib/format';

export interface LegendRow<K extends string> {
  key: K;
  label: string;
  note?: string;
  /** Токен образца: тот же смысл, что тон слоя карты. */
  swatch: string;
  count: number;
  /** Вторая строка числа: гектары, хозяйства, объём. */
  sub?: ReactNode;
}

/**
 * Легенда карты, она же фильтр: нажатие строки оставляет на карте только
 * этот статус (несколько — объединение). Пустые статусы не нажимаются.
 */
export function LegendFilter<K extends string>({
  rows,
  selected,
  onToggle,
  unit,
  label,
  foot,
}: {
  rows: LegendRow<K>[];
  selected: ReadonlySet<K>;
  onToggle: (key: K) => void;
  /** Формы слова счётчика: ['контур', 'контура', 'контуров']. */
  unit: (n: number) => string;
  label: string;
  foot?: ReactNode;
}) {
  return (
    <ul className="cab-legend" aria-label={label}>
      {rows.map((r) => {
        const on = selected.has(r.key);
        return (
          <li key={r.key}>
            <button type="button" className="cab-legend__row focus-ring" aria-pressed={on} disabled={r.count === 0} onClick={() => onToggle(r.key)}>
              <i className="cab-legend__swatch" style={{ background: `var(${r.swatch})` }} aria-hidden />
              <span className="cab-legend__label">
                <strong>{r.label}</strong>
                {r.note ? <small>{r.note}</small> : null}
              </span>
              <span className="cab-legend__num">
                <strong>{num(r.count)}</strong> <small>{unit(r.count)}</small>
                {r.sub ? <small className="cab-legend__sub">{r.sub}</small> : null}
              </span>
            </button>
          </li>
        );
      })}
      {foot ? <li className="cab-legend__foot">{foot}</li> : null}
    </ul>
  );
}

/** Переключить ключ во множестве (новое множество — для состояния React). */
export function toggled<K>(set: ReadonlySet<K>, key: K): Set<K> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Поменять несколько параметров адреса одним шагом (`null` — снять). */
export function usePatchParams(): (next: Record<string, string | null>) => void {
  const [, setParams] = useSearchParams();
  return useCallback(
    (next: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(next)) {
            if (v === null || v === '') copy.delete(k);
            else copy.set(k, v);
          }
          return copy;
        },
        { replace: true },
      ),
    [setParams],
  );
}

/**
 * Параметр экрана в адресе (`?wcycle=`, `?watering=`): выбор переживает
 * перезагрузку и пересылку ссылки, «назад» не засоряется (`replace`).
 */
export function useUrlParam(name: string): [string | null, (value: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(name);
  const set = useCallback(
    (next: string | null) =>
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          if (next === null || next === '') copy.delete(name);
          else copy.set(name, next);
          return copy;
        },
        { replace: true },
      ),
    [name, setParams],
  );
  return [value, set];
}
